import {
  type FormMetadata,
  getFormProps,
  getInputProps,
} from "@conform-to/react";
import { i18n } from "@lingui/core";
import { Trans, t } from "@lingui/macro";
import { InfoCircle } from "@winamp/icons/InfoCircle.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Checkbox } from "@winamp/ui/components/Checkbox/Checkbox.js";
import { CheckboxField } from "@winamp/ui/components/Checkbox/CheckboxField.js";
import { Description } from "@winamp/ui/components/Fieldset/Description.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { Field } from "@winamp/ui/components/Fieldset/Field.js";
import { Fieldset } from "@winamp/ui/components/Fieldset/Fieldset.js";
import { Label } from "@winamp/ui/components/Fieldset/Label.js";
import { Input } from "@winamp/ui/components/Input/Input.js";
import { Listbox } from "@winamp/ui/components/Listbox/Listbox.js";
import { ListboxDescription } from "@winamp/ui/components/Listbox/ListboxDescription.js";
import { ListboxLabel } from "@winamp/ui/components/Listbox/ListboxLabel.js";
import { ListboxOption } from "@winamp/ui/components/Listbox/ListboxOption.js";
import { Message } from "@winamp/ui/components/Message/Message.js";
import { MessageContent } from "@winamp/ui/components/Message/MessageContent.js";
import { MessageDescription } from "@winamp/ui/components/Message/MessageDescription.js";
import { MessageIcon } from "@winamp/ui/components/Message/index.js";
import { Switch } from "@winamp/ui/components/Switch/Switch.js";
import { SwitchField } from "@winamp/ui/components/Switch/SwitchField.js";
import { Fragment, useState } from "react";
import { Form, useLoaderData } from "react-router";
import { KYCFeedback } from "~/components/KYCFeedback";
import { ServiceSubmitButton } from "~/components/ServiceSubmitButton";
import { useIsSubmitting } from "~/hooks/use-is-submitting";
import { type Release, isReleaseEditable } from "~/models/release";
import {
  type ReleaseCandidateDetails,
  isReleaseCandidate,
} from "~/models/release-candidate";
import { ServiceEnum } from "~/models/service";
import { useAppContext } from "~/modules/auth/hooks";
import { useContextPath } from "~/modules/routing/use-context-path";
import type { ReleaseInfo } from "~/routes/_app+/distribution+/form/use-edit-release-form";
import type { loader as editLoader } from "../$id+";
import type { loader as createLoader } from "../new";

interface IReleaseFormProps {
  canSubmit: boolean;
  form: FormMetadata<ReleaseInfo>;
  fields: ReturnType<FormMetadata<ReleaseInfo>["getFieldset"]>;
  release: Release | ReleaseCandidateDetails;
}

const releaseDate = new Date();
const todayString = releaseDate.toISOString().split("T")[0];
releaseDate.setDate(releaseDate.getDate() + 7);
const todayPlus7String = releaseDate.toISOString().split("T")[0];

export function ReleaseForm({
  canSubmit,
  fields,
  form,
  release,
}: IReleaseFormProps) {
  const { individualTrackPricing, albumTrackPricing, userPreferences } =
    useLoaderData<typeof editLoader | typeof createLoader>();
  const isDraftSubmitting = useIsSubmitting({
    formDataMatches: { action: "save-as-draft" },
  });
  const isSubmitting = useIsSubmitting({
    formDataMatches: { action: "submit" },
  });

  const contextPath = useContextPath();
  const context = useAppContext();

  const [isReleaseDateVisible, setIsReleaseDateVisible] = useState(
    fields.showCustomReleaseDate.value === "on",
  );

  const dsps = fields.dspIds.getFieldList();
  const canBeEdited = isReleaseEditable(release);

  return (
    <Fragment>
      <Form {...getFormProps(form)} method="post">
        <Fieldset className="flex flex-col gap-14" disabled={!canBeEdited}>
          {dsps.map((dsp) => (
            <input
              key={dsp.key}
              type="hidden"
              name={fields.dspIds.name}
              defaultValue={dsp.initialValue}
            />
          ))}
          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-bold leading-7 text-left">
              <Trans>Release information</Trans>
            </h2>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col">
                  <h3 className="text-sm/5 font-medium text-primary">
                    <Trans>Release date</Trans>
                  </h3>
                  <span className="text-sm/5 font-normal text-secondary">
                    <Trans>
                      Your release may not show up in stores until{" "}
                      {i18n.date(releaseDate)} due to stores delivery times.
                    </Trans>
                  </span>
                </div>
                <CheckboxField className={"flex gap-3 my-1"}>
                  <Label>As soon as possible</Label>
                  <Checkbox
                    meta={fields.showCustomReleaseDate}
                    onChange={setIsReleaseDateVisible}
                  />
                </CheckboxField>

                <Field hidden={isReleaseDateVisible}>
                  <Input
                    {...getInputProps(fields.preferredReleaseDate, {
                      type: "date",
                    })}
                    placeholder={"YYYY-MM-DD"}
                    className="w-full lg:w-1/2"
                    min={todayPlus7String}
                  />
                  <ErrorMessages errors={fields.preferredReleaseDate.errors} />
                </Field>
              </div>
              <Field>
                <section className="flex gap-4">
                  <SwitchField>
                    <Label className="flex flex-row gap-2 items-center">
                      <Trans>Is this a re-release?</Trans>
                      <InfoCircle className="text-tertiary" />
                    </Label>
                    <Description>
                      <Trans>
                        If this album was previously released through another
                        distributor, you can provide the original release date
                        alongside the new release date for distribution through
                        us.
                      </Trans>
                    </Description>
                    <Switch meta={fields.showReReleaseFields} />
                    <ErrorMessages errors={fields.showReReleaseFields.errors} />
                  </SwitchField>
                </section>
              </Field>
              <Fieldset hidden={fields.showReReleaseFields.value !== "on"}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Field>
                    <Label
                      className="flex flex-row gap-2 items-center"
                      htmlFor={fields.originalReleaseDate.id}
                    >
                      <Trans>First release date</Trans>
                      <InfoCircle className="text-tertiary" />
                    </Label>
                    <Input
                      {...getInputProps(fields.originalReleaseDate, {
                        type: "date",
                      })}
                      placeholder={"YYYY-MM-DD"}
                      max={todayString}
                    />
                    <ErrorMessages errors={fields.originalReleaseDate.errors} />
                  </Field>
                  <Field>
                    <Label htmlFor={fields.ean.id}>
                      <Trans>UPC or EAN</Trans>
                    </Label>
                    <Input
                      {...getInputProps(fields.ean, {
                        type: "text",
                      })}
                      placeholder={
                        "Automatically generated if you don't have one"
                      }
                    />
                    <ErrorMessages errors={fields.ean.errors} />
                  </Field>
                </div>
              </Fieldset>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-bold leading-7 text-left">
              <Trans>Download pricing</Trans>
            </h2>
            <Field>
              <Label
                htmlFor={fields.trackPriceCategoryId.id}
                className="flex flex-row gap-2 items-center"
              >
                <Trans>Individual track pricing</Trans>
                <InfoCircle className="text-tertiary" />
              </Label>
              <Listbox
                meta={fields.trackPriceCategoryId}
                placeholder={t`Select an individual pricing`}
              >
                {individualTrackPricing.map((itp) => {
                  const prices = itp.prices.filter(
                    (price) =>
                      price.price.currencyCode ===
                      (userPreferences?.currency.currencyCode ?? "EUR"),
                  );

                  return (
                    <ListboxOption key={itp.value} value={itp.value}>
                      <ListboxLabel>{itp.label}</ListboxLabel>
                      <ListboxDescription>
                        {prices.length > 0
                          ? prices
                              .map((price) => {
                                const dpsName = price.dspName;
                                const amount = Intl.NumberFormat(i18n.locale, {
                                  style: "currency",
                                  currency: price.price.currencyCode,
                                }).format(price.price.value);
                                return t`${amount} on ${dpsName}`;
                              })
                              .join(", ")
                          : itp.description}
                      </ListboxDescription>
                    </ListboxOption>
                  );
                })}
              </Listbox>
              <ErrorMessages errors={fields.trackPriceCategoryId.errors} />
            </Field>
            <div className="flex flex-col gap-6">
              <Field>
                <Label
                  htmlFor={fields.albumPriceCategoryId.id}
                  className="flex flex-row gap-2 items-center"
                >
                  <Trans>Album track pricing</Trans>
                  <InfoCircle className="text-tertiary" />
                </Label>
                <Listbox
                  meta={fields.albumPriceCategoryId}
                  placeholder={t`Select a pricing`}
                >
                  {albumTrackPricing.map((atp) => {
                    const prices = atp.prices.filter(
                      (price) =>
                        price.price.currencyCode ===
                        (userPreferences?.currency.currencyCode ?? "EUR"),
                    );

                    return (
                      <ListboxOption key={atp.value} value={atp.value}>
                        <ListboxLabel>{atp.label}</ListboxLabel>
                        <ListboxDescription>
                          {prices.length > 0
                            ? prices
                                .map((price) => {
                                  const dpsName = price.dspName;
                                  const amount = Intl.NumberFormat(
                                    i18n.locale,
                                    {
                                      style: "currency",
                                      currency: price.price.currencyCode,
                                    },
                                  ).format(price.price.value);
                                  return t`${amount} on ${dpsName}`;
                                })
                                .join(", ")
                            : atp.description}
                        </ListboxDescription>
                      </ListboxOption>
                    );
                  })}
                </Listbox>
                <ErrorMessages errors={fields.albumPriceCategoryId.errors} />
              </Field>
              <Message className="flex flex-col gap-2">
                <MessageContent>
                  <MessageDescription>
                    <Trans>
                      The prices listed above are for informational purposes
                      only. The DSP retain the right to modify without prior
                      notice their own sales prices at their discretion. The
                      company will not be held responsible for any damages,
                      whether direct or indirect, that may arise from the
                      information provided on this page. The information is
                      given “as is” and without any express, implicit, or legal
                      warranties.
                    </Trans>
                  </MessageDescription>
                </MessageContent>
              </Message>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-bold leading-7 text-left">
              <Trans>Territories</Trans>
            </h2>
            <SwitchField>
              <Label>
                <Trans>Exclude certain countries of this release</Trans>
              </Label>
              <Switch meta={fields.territories} disabled />
              <ErrorMessages errors={fields.territories.errors} />
            </SwitchField>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-bold leading-7 text-left">
              <Trans>Select release partners</Trans>
            </h2>
            <SwitchField>
              <Label>
                <Trans>Select All</Trans>
              </Label>
              <Switch meta={fields.releasePartners} defaultChecked disabled />
              <ErrorMessages errors={fields.releasePartners.errors} />
            </SwitchField>
          </section>

          {form.errors && form.errors.length > 0 ? (
            <Message type="error" className="-my-4">
              <MessageIcon />
              <MessageContent>
                <MessageDescription>
                  {form.errors.join("\n")}
                </MessageDescription>
              </MessageContent>
            </Message>
          ) : null}

          {canBeEdited && (
            <section className="flex flex-wrap gap-4">
              <ServiceSubmitButton
                form={form.id}
                name="action"
                value="submit"
                isSubmitting={isSubmitting}
                serviceId={ServiceEnum.Distribution}
                isServiceActivated={
                  !!context.artistServicesStatus?.distribution.activated
                }
                disabled={!canSubmit}
                activationLabel={t`Accept Terms & submit`}
                enableMetadataWarning={
                  isReleaseCandidate(release) && !release.isUsed
                }
              >
                <Trans>Distribute to DSP</Trans>
              </ServiceSubmitButton>
              <Button
                variant="secondary"
                type="submit"
                name="action"
                value="save-as-draft"
                disabled={isSubmitting || isDraftSubmitting}
                loading={isDraftSubmitting}
              >
                <Trans>Save and quit</Trans>
              </Button>

              <Button
                variant="ghost"
                to={contextPath("/distribution")}
                disabled={isSubmitting || isDraftSubmitting}
              >
                <Trans>Cancel</Trans>
              </Button>
            </section>
          )}
        </Fieldset>
        <KYCFeedback className="mt-12">
          <Trans>
            Since your identity has not been verified, we have set your tracks
            as drafts. Please verify your identity and republish them to
            distribute on streaming platforms.
          </Trans>
        </KYCFeedback>
      </Form>
    </Fragment>
  );
}
